import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Store, Search, MapPin, Tag, Phone, Globe, Mail, Instagram, Facebook, Youtube, Linkedin, ArrowRight } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube, linkedin: Linkedin };

const CAT_KEYS = ["restauracio", "moda", "salut", "cultura", "serveis", "alimentacio", "esports", "altres", "general"];
const SALOU_CENTER = [41.0764, 1.1414];

export default function Directory() {
  const { t } = useTranslation();
  const cat = (k) => t(`directory.cats.${k}`, { defaultValue: k });
  const [associates, setAssociates] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api.get("/directory").then(({ data }) => {
      setAssociates(data.associates || []);
      setPromotions(data.promotions || []);
    });
    // Also fetch social media by loading full associate details is not exposed publicly.
    // Instead, /api/directory already returns social if we extend it — for now, /api/associates has it (auth-only).
    // Public directory now includes social in filtered payload (see backend patch).
  }, []);

  const filtered = associates.filter((a) => {
    if (category !== "all" && a.category !== category) return false;
    if (q && !a.commercial_name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const filteredForMap = filtered.filter(a => a.lat && a.lng);
  const activePromos = promotions.filter((p) => {
    const now = new Date();
    return new Date(p.start_date) <= now && new Date(p.end_date) >= now;
  });

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="overline text-accent">{t("directory.overline")}</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight">{t("directory.title")}</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">{t("directory.subtitle", { count: associates.length, promos: activePromos.length })}</p>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          <div className="relative flex-1 min-w-56">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input placeholder={t("directory.search_ph")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full" data-testid="directory-search-input" aria-label={t("directory.search_ph")} />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-full w-52" data-testid="directory-category-filter" aria-label={t("incidents.category")}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("directory.all_categories")}</SelectItem>
              {CAT_KEYS.map((k) => <SelectItem key={k} value={k}>{cat(k)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="grid">
          <TabsList className="rounded-full mb-6">
            <TabsTrigger value="grid" className="rounded-full" data-testid="directory-tab-grid">{t("directory.tab_grid")}</TabsTrigger>
            <TabsTrigger value="promotions" className="rounded-full" data-testid="directory-tab-promos">{t("directory.tab_promos", { count: activePromos.length })}</TabsTrigger>
          </TabsList>

          <TabsContent value="grid">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((a) => {
                const promos = activePromos.filter(p => p.associate_id === a.id);
                return (
                  <Card key={a.id} className="rounded-2xl border-2 hover:border-accent transition-colors duration-200" data-testid={`directory-card-${a.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Store className="w-5 h-5" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="font-heading font-semibold text-lg leading-tight truncate">{a.commercial_name}</div>
                          <div className="text-xs uppercase tracking-widest text-accent mt-1">{cat(a.category)}</div>
                        </div>
                      </div>
                      <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                        {a.address && <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{a.address}</span></div>}
                        {a.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" />{a.phone}</div>}
                        {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary hover:underline"><Globe className="w-3.5 h-3.5" />Web</a>}
                      </div>
                      {a.social && Object.entries(a.social).filter(([, v]) => v && v.trim()).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`directory-social-${a.id}`}>
                          {Object.entries(a.social).filter(([, v]) => v && v.trim()).map(([k, v]) => {
                            const Icon = SOCIAL_ICONS[k];
                            return (
                              <a key={k} href={v} target="_blank" rel="noreferrer" title={k} className="w-8 h-8 rounded-lg bg-secondary hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center capitalize text-[10px] font-bold" data-testid={`directory-social-${a.id}-${k}`}>
                                {Icon ? <Icon className="w-3.5 h-3.5" /> : k.slice(0, 2)}
                              </a>
                            );
                          })}
                        </div>
                      )}
                      {promos.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <div className="text-xs font-semibold uppercase tracking-widest text-accent mb-2 flex items-center gap-1"><Tag className="w-3 h-3" aria-hidden="true" /> {t(promos.length > 1 ? "directory.promo_count_plural" : "directory.promo_count", { count: promos.length })}</div>
                          {promos.slice(0, 2).map(p => (
                            <div key={p.id} className="text-sm py-1"><span className="font-medium">{p.title}</span></div>
                          ))}
                        </div>
                      )}
                      {a.member_number && (
                        <Link to={`/comerc/${a.member_number}`} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:gap-2 transition-[gap]" data-testid={`directory-profile-${a.id}`}>
                          {t("directory.view_profile")} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {filtered.length === 0 && <div className="col-span-full text-center py-12 text-muted-foreground">{t("directory.no_shops")}</div>}
            </div>
          </TabsContent>

          <TabsContent value="promotions">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activePromos.map((p) => {
                const assoc = associates.find(a => a.id === p.associate_id);
                return (
                  <Card key={p.id} className="rounded-2xl border-2 overflow-hidden" data-testid={`directory-promo-${p.id}`}>
                    {p.image_url ? <img src={p.image_url} alt="" className="aspect-video w-full object-cover" /> : <div className="aspect-video bg-primary/10 flex items-center justify-center"><Tag className="w-10 h-10 text-primary/40" /></div>}
                    <CardContent className="p-5">
                      <div className="text-[10px] uppercase tracking-widest text-accent font-semibold">{t("directory.promo_active")} · {cat(p.category)}</div>
                      <div className="font-heading font-semibold text-lg mt-1 leading-tight">{p.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{t("directory.promo_by", { name: assoc?.commercial_name || "—" })}</div>
                      <div className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.description}</div>
                      <div className="text-xs text-muted-foreground mt-2">{t("directory.until", { date: p.end_date?.slice(0, 10) })}</div>
                    </CardContent>
                  </Card>
                );
              })}
              {activePromos.length === 0 && <div className="col-span-full text-center py-12 text-muted-foreground">{t("directory.no_promos")}</div>}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-12">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="font-heading font-semibold text-xl">{t("directory.map_title")}</h2>
            <div className="text-xs text-muted-foreground" data-testid="map-count">
              <strong>{filteredForMap.length}</strong> / {associates.filter(a => a.lat && a.lng).length}
            </div>
          </div>
          <Card className="rounded-2xl border-2 overflow-hidden">
            <div style={{ height: 480 }} data-testid="directory-map">
              <MapContainer center={SALOU_CENTER} zoom={14} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OSM" />
                {filteredForMap.map(a => (
                  <Marker key={a.id} position={[a.lat, a.lng]}>
                    <Popup>
                      <div className="min-w-40" data-testid={`map-popup-${a.id}`}>
                        <div className="font-heading font-semibold text-sm">{a.commercial_name}</div>
                        <div className="text-[11px] uppercase tracking-widest text-accent mt-0.5">{cat(a.category)}</div>
                        {a.address && <div className="text-xs text-muted-foreground mt-1">{a.address}</div>}
                        {a.member_number && (
                          <Link to={`/comerc/${a.member_number}`} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                            {t("directory.view_profile")} →
                          </Link>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
}
